import {
  doc,
  collection,
  getDoc,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  QuerySnapshot,
  getDocs,
  Query,
  query,
  where,
  updateDoc,
  deleteDoc,
  QueryDocumentSnapshot,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../config';
import { UserCredential } from 'firebase/auth';
import { registerStatus, userRole } from '../../../../constants/enum';
import { RegisterStaffType } from '../../../../components/uiParts/StaffRegisterCard';
import { deleteFile, uploadFile } from '../../storage';
import { EditStaffType } from '../../../../components/uiParts/StaffEditCard';
import { handleDeleteUserFromAuth } from '../../function/handleDeleteUserFromAuth';
import { addMemberToGroup } from '../group';

export const usersRef = collection(db, 'users');
const userDocument = (uid: string): DocumentReference<DocumentData> => doc(usersRef, uid);
const userSnapshot = async (uid: string): Promise<DocumentSnapshot> =>
  await getDoc(userDocument(uid));

// ユーザー情報を取得
export const getUser = async (uid: string): Promise<DocumentData | undefined> => {
  const user = await userSnapshot(uid);
  return user.data();
};

// ユーザー情報を更新
export const editStaffUser = async (
  uid: string,
  watch: <K extends keyof EditStaffType>(key: K) => EditStaffType[K],
  fileIcon: FileList | null,
): Promise<void> => {
  // 更新データの作成
  const roleValue = watch('role');
  const role = typeof roleValue === 'boolean' ? roleValue : roleValue === 'true';
  const q = query(usersRef, where('uid', '==', uid));
  const querySnapshot = await getDocs(q);
  let oldFileIcon = '';
  querySnapshot.docs.forEach((document: QueryDocumentSnapshot<DocumentData>): void => {
    oldFileIcon = document.data().icon as string;
  });

  const fileIconName = fileIcon !== null ? fileIcon[0].name : '';
  const icon = fileIcon ? `users/${uid}/profile/${Date.now()}_${fileIconName}` : null;
  const dailyLimitValue = watch('daily_limit');
  let userData = {
    department: (watch('department') as string) || null,
    name: watch('name') as string,
    role: role ? userRole.admin : userRole.member,
    daily_limit: dailyLimitValue != null ? Number(dailyLimitValue) : null,
    updated_at: new Date(),
  };

  if (icon) {
    const { storagePath, iconUrl } = await uploadFile(fileIcon, icon);
    userData = { ...userData, ...{ icon: storagePath, iconUrl } };
    if (oldFileIcon) {
      deleteFile(oldFileIcon);
    }
  }

  // Firestore のデータを更新
  const userDoc = doc(usersRef, uid);
  await updateDoc(userDoc, userData);
};

// 全ユーザーを取得
export const getAllUsers = async (): Promise<DocumentData[]> => {
  const usersQuery: Query<DocumentData> = query(
    usersRef,
    where('registerStatus', '!=', registerStatus.deleted),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(usersQuery);
  return querySnapshot.docs.map(doc => doc.data());
};

// 有効なユーザーのみ取得
export const getValidUsers = async (): Promise<DocumentData[]> => {
  const usersQuery: Query<DocumentData> = query(
    usersRef,
    where('registerStatus', '==', registerStatus.finished),
  );
  const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(usersQuery);
  return querySnapshot.docs.map(doc => doc.data());
};

// ユーザーを削除
export const deleteUser = async (uid: string, loginUserUid: string): Promise<void> => {
  const userDocRef = userDocument(uid);
  let originalStatus, originalEmail;
  try {
    // 削除対象のユーザーを確認
    const userDoc = await getDoc(userDocRef);
    if (!userDoc.exists()) {
      throw new Error('ユーザーが見つかりません。');
    }
    originalStatus = userDoc.data().registerStatus;
    originalEmail = userDoc.data().email;

    // ステータスを削除に変更
    await updateDoc(userDocRef, {
      registerStatus: registerStatus.deleted,
      email: `${originalEmail}_${Date.now()}`,
      updated_at: new Date(),
      updated_by: loginUserUid,
    });

    // メール招待中のユーザーはauthが存在しないため削除しない
    if (originalStatus === registerStatus.emailRegistered) {
      return;
    } else {
      // Authからユーザーを削除
      await handleDeleteUserFromAuth(uid);
    }
  } catch (error) {
    // ロールバック: Firestoreのステータスを元に戻す
    await updateDoc(userDocRef, { registerStatus: originalStatus, email: originalEmail });

    throw new Error('ユーザーの削除に失敗しました。');
  }
};

// 招待が取り消されている場合のエラー
export class InviteCanceledError extends Error {
  constructor() {
    super('招待が取り消されています。管理者ユーザーから再度招待を受けてください。');
    this.name = 'InviteCanceledError';
  }
}

// ユーザーを追加
export const addStaffUser = async (
  email: string | null,
  createdUser: UserCredential,
  watch: <K extends keyof RegisterStaffType>(key: K) => RegisterStaffType[K],
  fileIcon: FileList | null,
): Promise<void> => {
  if (email) {
    email = email.split('?').join('');
  }

  const q = query(usersRef, where('email', '==', email));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);

  // 招待が取り消されている場合は、作成したAuthユーザーを削除する
  if (querySnapshot.empty) {
    await createdUser.user.delete();
    throw new InviteCanceledError();
  }

  let user: DocumentReference<DocumentData>;

  querySnapshot.docs.map(async (oldDoc: QueryDocumentSnapshot<DocumentData>): Promise<void> => {
    // 更新するusersコレクションのデータ
    const oldData = oldDoc.data();

    const userData = {
      created_at: oldData.created_at as Date,
      department: (watch('department') as string) || null,
      email,
      inviteFlg: oldData.inviteFlg as string,
      name: watch('name') as string,
      registerStatus: registerStatus.finished,
      role: oldData.role as string,
      uid: createdUser.user.uid,
      updated_at: new Date(),
    };

    // Userドキュメントの置き換え（authのuidとusers.idを合わせる）
    await setDoc(doc(db, 'users', createdUser.user.uid), userData);
    await deleteDoc(doc(usersRef, oldDoc.id));

    // 招待時に指定されたグループに新UIDで追加
    const pendingGroupIds = oldData.pendingGroupIds as string[] | undefined;
    if (pendingGroupIds && pendingGroupIds.length > 0) {
      await Promise.all(
        pendingGroupIds.map(groupId => addMemberToGroup(groupId, createdUser.user.uid)),
      );
    }

    user = doc(db, 'users', createdUser.user.uid);
    const userSnap = await getDoc(user);
    const newUid = userSnap.data()?.uid as string;
    const fileIconName = fileIcon !== null ? fileIcon[0].name : '';
    const icon = fileIcon !== null ? `users/${newUid}/profile/${Date.now()}_${fileIconName}` : null;

    // iconがある場合はアップロード
    if (icon !== null) {
      const { storagePath, iconUrl } = await uploadFile(fileIcon, icon);
      const userUpdateData = {
        icon: storagePath,
        iconUrl: iconUrl,
        updated_at: new Date(),
      };
      await updateDoc(doc(db, 'users', newUid), userUpdateData);
    }
  });
};

// メールアドレスからユーザーを取得
export const userFindByEmail = async (email: string): Promise<boolean> => {
  const userQuery = query(usersRef, where('email', '==', email));
  const { empty, size } = await getDocs(userQuery);

  return !(empty || size >= 2);
};

interface UserData {
  email: string;
  registerStatus?: number;
}

// メールアドレスからユーザーを取得
export const getUserRegisterStatusByEmail = async (email: string): Promise<UserData | null> => {
  const userQuery = await query(usersRef, where('email', '==', email));
  const querySnapshot = await getDocs(userQuery);

  if (querySnapshot.empty || querySnapshot.size >= 2) {
    return null; // ユーザーが見つからないか、複数のユーザーが見つかった場合
  }

  // 最初のドキュメントのデータを取得
  const userData = querySnapshot.docs[0].data() as UserData;
  return userData;
};

// メール送信時に匿名ユーザーを作成する
export const createStaffUserWhenSendemail = async (
  email: string,
  watch: (arg0: string) => string | boolean | null,
  groupIds?: string[],
): Promise<void> => {
  const newDocRef = doc(usersRef);

  const data: Record<string, unknown> = {
    uid: newDocRef.id,
    created_at: new Date(),
    email,
    inviteFlg: true,
    registerStatus: registerStatus.emailRegistered,
    role:
      (watch('role') as string | boolean | null) === 'true'
        ? userRole.admin
        : (watch('role') as string | boolean | null) === 'false'
          ? userRole.member
          : (watch('role') as string | boolean | null)
            ? userRole.admin
            : userRole.member,
    updated_at: new Date(),
  };

  // 招待時に指定されたグループIDを保存（登録完了時に処理する）
  if (groupIds && groupIds.length > 0) {
    data.pendingGroupIds = groupIds;
  }

  // ドキュメントを作成
  await setDoc(newDocRef, data);
};
